import { memo, type FC } from 'react'
import { useCallback, useEffect, useState, useRef } from 'react'
import useTracksStore, {
  useMasteringAvailability,
} from '@/shared/store/tracks.store.tsx'
import useAudioStore from '@/shared/store/audio.store.tsx'
import { useShallow } from 'zustand/react/shallow'
import { formatSecondsToMinutesSeconds } from '@/utils/formatters.ts'
import Icon from '../common/Icon.tsx'
import Button from '../common/Button.tsx'
import { useApplyMastering } from '@/hooks/useApplyMastering.ts'
import { MediaFileService } from '@/features/media-file/data/media-file.service.ts'
import { useToast } from '../../shared/hooks/ToastProvider.tsx'
import { useTranslation } from 'react-i18next'
import InteractiveAudioWave from '../common/InteractiveAudioWave.tsx'
import Spinner from '../common/Spinner.tsx'
import { useTracking } from '@/providers/TrackingProvider.tsx'
import { trackAudioformDownload } from '@/utils/track-audioform-download.ts'

const MasterSection: FC<{ isLoading: boolean }> = ({ isLoading }) => {
  const {
    applyMastering,
    loading: applyingMastering,
    error: applyMasteringError,
  } = useApplyMastering()
  const isMasterAvailable = useMasteringAvailability()
  const { t } = useTranslation()
  const AUDIO_ID = 'master-mix'
  const { showToast } = useToast()
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerWidth, setContainerWidth] = useState(900)
  const { generatedMix, isMixEmpty } = useTracksStore(
    useShallow((state) => ({
      generatedMix: state.generatedMix,
      isMixEmpty: state.isMixEmpty,
    }))
  )

  const {
    playAudioBuffer,
    pausePlayback,
    isPlaying,
    progress,
    currentAudioId,
    setProgress,
    isPaused,
  } = useAudioStore(
    useShallow((state) => ({
      playAudioBuffer: state.playAudioBuffer,
      pausePlayback: state.pausePlayback,
      isPlaying: state.isPlaying,
      progress: state.progress,
      currentAudioId: state.currentAudioId,
      setProgress: state.setProgress,
      isPaused: state.isPaused,
    }))
  )
  const [isCurrentAudioInContext, setIsCurrentAudioInContext] = useState(false)
  const [isCurrentAudioPlaying, setIsCurrentAudioPlaying] = useState(false)
  const [playbackProgress, setPlaybackProgress] = useState(0)
  const { trackEvent } = useTracking()

  useEffect(() => {
    const isInContext = currentAudioId === AUDIO_ID
    setIsCurrentAudioInContext(isInContext)
    setIsCurrentAudioPlaying(isInContext && isPlaying)
  }, [currentAudioId, isCurrentAudioInContext, isPlaying])

  useEffect(() => {
    if (isCurrentAudioPlaying) {
      setPlaybackProgress(progress)
    }
  }, [progress, isCurrentAudioPlaying])

  useEffect(() => {
    if (applyMasteringError) {
      showToast(applyMasteringError.message)
    }
  }, [applyMasteringError])

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setContainerWidth(containerRef.current.clientWidth - 46)
      }
    }

    const rafId = requestAnimationFrame(updateWidth)

    window.addEventListener('resize', updateWidth)
    return () => {
      cancelAnimationFrame(rafId)
      window.removeEventListener('resize', updateWidth)
    }
  }, [generatedMix])

  const onPlayPauseClick = useCallback(() => {
    if (isCurrentAudioPlaying && !isPaused) {
      void pausePlayback()
    } else if (generatedMix) {
      trackEvent('ProEditor - Previewed audio - MasterSection', {
        mixDuration: generatedMix.audioBuffer.duration,
        mixUrl: generatedMix.url,
        isCurrentAudioInContext,
        playbackProgress,
      })
      if (isCurrentAudioInContext) {
        void playAudioBuffer(AUDIO_ID, generatedMix.audioBuffer)
      } else {
        void playAudioBuffer(
          AUDIO_ID,
          generatedMix.audioBuffer,
          playbackProgress
        )
      }
    }
  }, [
    isCurrentAudioInContext,
    isCurrentAudioPlaying,
    isPaused,
    playbackProgress,
    generatedMix,
    trackEvent,
  ])

  const updateProgress = useCallback(
    (newProgress: number) => {
      if (isCurrentAudioInContext) {
        setProgress(newProgress)
      } else if (generatedMix) {
        void playAudioBuffer(AUDIO_ID, generatedMix.audioBuffer, newProgress)
      }
    },
    [isCurrentAudioInContext, generatedMix, setProgress]
  )

  const handleDownloadMix = useCallback(() => {
    if (!generatedMix) return
    trackAudioformDownload(trackEvent, {
      assetType: 'mix',
      audioformId: generatedMix.id,
      format: 'wav',
    })

    void MediaFileService.downloadMediaFile(generatedMix.url, 'mix.wav')
  }, [generatedMix, trackEvent])

  return (
    <div className="master-section" data-visible={!(isLoading || isMixEmpty)}>
      {!applyingMastering && !generatedMix && (
        <div className="flex flex-col items-center gap-4">
          <span>{t('ls_apply_mastering')}</span>
          <Button onClick={applyMastering} disabled={!isMasterAvailable}>
            <Icon name="sparkles" width={16} />
            {t('ls_apply_mastering_cta')}
          </Button>
        </div>
      )}
      {applyingMastering && (
        <div className="flex items-center gap-4">
          <Spinner className="h-8 w-8" />
          <span>{t('ls_applying_mastering')}</span>
        </div>
      )}
      {generatedMix && !applyingMastering && (
        <div
          className="w-max-[min(1500px,_80%)] flex w-full items-center gap-10"
          style={{ minWidth: '920px', maxWidth: 'min(1500px, 80%)' }}
        >
          <button type="button" onClick={onPlayPauseClick}>
            <Icon
              name={isCurrentAudioPlaying && !isPaused ? 'pause' : 'play'}
              height={40}
              width={40}
            />
          </button>
          <div
            ref={containerRef}
            className="relative flex h-14 w-full items-center gap-6"
          >
            <div className="absolute left-0 top-0 flex w-full items-center gap-6">
              <div className="w-full">
                <InteractiveAudioWave
                  audioBuffer={generatedMix.audioBuffer}
                  width={containerWidth}
                  height={56}
                  progress={isCurrentAudioInContext ? progress : 0}
                  setProgress={updateProgress}
                  spacing={0.5}
                  lineWidth={2}
                  isInteractive={true}
                />
              </div>
              <span className="text-sm font-semibold text-white">
                {formatSecondsToMinutesSeconds(
                  generatedMix.audioBuffer.duration
                )}
              </span>
            </div>
          </div>
          <button type="button" onClick={handleDownloadMix}>
            <Icon name="download" fill="white" width={40} height={40} />
          </button>
        </div>
      )}
    </div>
  )
}

MasterSection.displayName = 'MasterSection'
export default memo(MasterSection)
